import { authAPI } from "./axiosConfig";
import { jwtDecode } from "jwt-decode";
import { login, logout, setProfile } from "../redux/action";
const setAuthHeader = () => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    authAPI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};
export const register = async (userData) => {
  try {
    const response = await authAPI.post("/api/register/", userData);
    return response.data;
  } catch (error) {
    console.error("Registration error:", error.response.data);
    throw error;
  }
};

export const handleLogin = (credentials) => async (dispatch) => {
  try {
    let locationInfo = "";
    try {
      const ipInfoResponse = await fetch("https://ipinfo.io/json");
      if (ipInfoResponse.ok) {
        const ipData = await ipInfoResponse.json();
        locationInfo = JSON.stringify({
          ip: ipData.ip,
          city: ipData.city,
          region: ipData.region,
          country: ipData.country,
          loc: ipData.loc,
        });
      }
    } catch (ipError) {
      console.log("Could not fetch location info:", ipError);
    }

    const loginData = {
      ...credentials,
      location_info: locationInfo,
    };

    const response = await authAPI.post("/api/login/", loginData);
    const { access } = response.data;
    const decodedToken = jwtDecode(access);
    const expirationTime = decodedToken.exp * 1000;

    localStorage.setItem("accessToken", access);
    dispatch(
      login({
        token: access,
        expirationTime,
      })
    );

    return response.data;
  } catch (error) {
    console.error("Login error:", error.response?.data || error.message);
    throw error;
  }
};
export const handleLogout = () => (dispatch) => {
  // Set the logout flag first
  localStorage.setItem('isLoggingOut', 'true');
  // Remove tokens
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  // Dispatch logout action
  dispatch(logout());
  // Clear the flag after a short delay to ensure it's available during the redirect
  setTimeout(() => {
    localStorage.removeItem('isLoggingOut');
  }, 1000);
};
export const restoreAuth = () => (dispatch) => {
  const token = localStorage.getItem("accessToken");

  if (token) {
    try {
      const decodedToken = jwtDecode(token);
      const expirationTime = decodedToken.exp * 1000;

      if (Date.now() < expirationTime) {
        dispatch(
          login({
            token,
            expirationTime,
          })
        );
      } else {
        dispatch(logout());
      }
    } catch (error) {
      dispatch(logout());
    }
  }
};

import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";

export const useAutoLogout = () => {
  const dispatch = useDispatch();
  const { expirationTime } = useSelector((state) => state.auth || {});

  useEffect(() => {
    if (expirationTime) {
      const timeRemaining = expirationTime - Date.now();

      if (timeRemaining <= 0) {
        dispatch(logout());
      } else {
        const timer = setTimeout(() => {
          dispatch(logout());
        }, timeRemaining);

        return () => clearTimeout(timer);
      }
    }
  }, [expirationTime, dispatch]);
};

export const refreshToken = async () => {
  try {
    const refresh = localStorage.getItem("refreshToken");
    const response = await authAPI.post("/api/token/refresh/", { refresh });

    localStorage.setItem("accessToken", response.data.access);

    return response.data;
  } catch (error) {
    console.error("Token refresh error:", error.response.data);
    throw error;
  }
};


export const getProfile = () => async (dispatch) => {
  setAuthHeader();
  try {
    const response = await authAPI.get("api/me/");
    dispatch(setProfile(response.data));
    return response.data;
  } catch (error) {
    console.error("Failed to fetch user profile:", error);
    throw error;
  }
};

export const updateProfile = async (profileData) => {
  return await authAPI.put("/api/me/", profileData);
};


export const getAllUserGroups = async () => {
  setAuthHeader();
  try {
    const response = await authAPI.get("/api/user-groups/");
    return response.data;
  } catch (error) {
    console.error("Failed to fetch user groups:", error);
    throw error;
  }
};

export const getUserGroupById = async (id) => {
  setAuthHeader();
  try {
    const response = await authAPI.get(`/api/user-groups/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch user group with ID ${id}:`, error);
    throw error;
  }
};

export const createUserGroup = async (data) => {
  setAuthHeader();
  try {
    const response = await authAPI.post("/api/user-groups/", data);
    return response.data;
  } catch (error) {
    console.error("Failed to create user group:", error);
    throw error;
  }
};

export const updateUserGroup = async (id, data) => {
  setAuthHeader();
  try {
    const response = await authAPI.put(`/api/user-groups/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Failed to update user group ${id}:`, error);
    throw error;
  }
};

export const deleteUserGroup = async (id) => {
  setAuthHeader();
  try {
    await authAPI.delete(`/api/user-groups/${id}/`);
    return true;
  } catch (error) {
    console.error(`Failed to delete user group ${id}:`, error);
    throw error;
  }
};


export const getPagePermissions = async () => {
  setAuthHeader(); // ensure token is set
  try {
    const response = await authAPI.get("/api/page-permissions/");
    return response.data;
  } catch (error) {
    console.error("Failed to fetch page permissions:", error);
    throw error;
  }
};


export const sendOtpToEmail = async (email) => {
  setAuthHeader(); 
  try {
    const response = await authAPI.post("/api/send-otp/", { email });
    return response.data;
  } catch (error) {
    console.error("Failed to send OTP:", error);
    throw new Error("Could not send OTP. Please try again.");
  }
};

export const verifyAdmin = async (password) => {
  setAuthHeader(); 
  try {
    const response = await authAPI.post("/api/verify-admin-password/", {
      password
    });
    return response.data;
  } catch (error) {
    console.error("Admin verification failed:", error);
    return { valid: false };
  }
};

export const checkUserDuplicates = async ({ email, username, user_id }) => {
  setAuthHeader(); 
  try {
    const response = await authAPI.post("api/check-user-duplicates/", {
      email,
      username,
      user_id,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to check duplicates:", error);
    throw new Error("Could not check user duplicates. Please try again.");
  }
};

/**
 * Fetch user-specific route permissions from the backend
 * This is used for the new access control system where the backend
 * determines which frontend routes a user can access
 */
export const getUserPermissions = async () => {
  setAuthHeader();
  try {
    console.log("[getUserPermissions] Fetching user permissions...");
    const response = await authAPI.get("/api/user-permissions/");
    const userData = response.data;
    
    console.log("[getUserPermissions] Raw user data received:", userData);
    
    // Handle the new page_permissions structure
    // If we have page_permissions in the response, use it directly
    if (userData.page_permissions) {
      console.log("[getUserPermissions] Found page_permissions in response:", userData.page_permissions);
      // Also extract routes from page_permissions for backward compatibility
      const routes = Object.keys(userData.page_permissions);
      console.log("[getUserPermissions] Extracted routes from page_permissions:", routes);
      
      const result = {
        ...userData,
        routes: routes,
        page_permissions: userData.page_permissions
      };
      
      console.log("[getUserPermissions] Final processed permissions:", result);
      return result;
    } else {
      // Fallback to old structure if page_permissions is not present
      console.log("[getUserPermissions] No page_permissions found, using old structure");
      console.log("[getUserPermissions] Final permissions (old structure):", userData);
      return userData;
    }
  } catch (error) {
    console.error("Failed to fetch user permissions:", error);

    // If this is a network / CORS error (no response), fall back to
    // granting full permissions so the app remains usable (especially in staging).
    if (!error.response) {
      console.warn(
        "[getUserPermissions] Network/CORS error detected, falling back to hasAllPermissions=true"
      );
      return {
        routes: [],
        role: null,
        hasAllPermissions: true,
        page_permissions: {},
      };
    }

    throw new Error("Could not fetch user permissions. Please try again.");
  }
};

/**
 * Update user-specific route permissions in the backend
 * This is used by the access control page to save a user's route permissions
 * @param {number} userId - ID of the user to update permissions for
 * @param {array} permissions - Array of route paths the user should have access to
 */
export const updateUserPermissions = async (userId, permissions) => {
  setAuthHeader();
  try {
    const response = await authAPI.put("/api/user-permissions/", {
      user_id: userId,
      permissions: permissions
    });
    return response.data;
  } catch (error) {
    console.error("Failed to update user permissions:", error);
    throw new Error("Could not update user permissions. Please try again.");
  }
};
